/*
 * Copyright (c) 2025-2026 Terra Technology (Guangzhou) Co., Ltd.
 * Copyright (c) 2024-2026 山东艾坦迪数智科技有限公司.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 *
 */

import { AvatarDropdown, AvatarName, Footer, IconMap, NoticeBanner, NoticeIcon, Question, SelectLang } from '@/components';
import Icon, { LinkOutlined } from '@ant-design/icons';
import type { MenuDataItem, Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

import { fetchCurrentUser } from "@/apis";
import { getLogoInfo } from "@/apis/system/config";
import TabsLayout, { TabsLayoutProps } from "@/components/TabsLayout";
import { LOGIN_PATH } from "@/config/constants";
import { getToken, removeToken } from "@/utils/auth";
import { generateAvatar } from "@/utils/avatar";
import React from 'react';

const isDev = process.env.NODE_ENV === 'development';

export const getCustomTabs = () => (props: TabsLayoutProps) => {
  return <TabsLayout {...props} />
}

const handleIcon = (icon?: React.ReactNode) => {
  return typeof icon === 'string' && icon.includes('|svg')
    ? <Icon component={IconMap[icon.replace('|svg', '')]} />
    : icon;
};

const menuDataRender = (menuData: MenuDataItem[]): MenuDataItem[] => {
  return menuData.map(item => {
    let icon = handleIcon(item.icon);
    let children = item.children ? menuDataRender(item.children) : undefined;
    return {
      ...item,
      icon,
      children
    }
  });
}

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  layoutSettings?: Partial<LayoutSettings>;
  currentUser?: CurrentUser;
  fetchUserInfo?: () => Promise<CurrentUser | undefined>;
  logoUrl?: string;
  sysTitle?: string;
}> {
  // 动态读取 sys_config 中的 Logo 与系统标题，并同步浏览器 favicon
  let logoUrl: string = defaultSettings.logo as string;
  let sysTitle: string = (defaultSettings.title as string) || '';
  try {
    const logoResult = await getLogoInfo();
    if (logoResult.success && logoResult.data) {
      if (logoResult.data.sysLogo) {
        logoUrl = logoResult.data.sysLogo;
      }
      if (logoResult.data.sysTitle) {
        sysTitle = logoResult.data.sysTitle;
      }
      if (logoResult.data.browserLogo) {
        const existingLinks = document.querySelectorAll("link[rel*='icon']");
        existingLinks.forEach((link) => link.remove());
        const newLink = document.createElement('link');
        newLink.rel = 'shortcut icon';
        newLink.href = `${logoResult.data.browserLogo}?t=${Date.now()}`;
        document.head.appendChild(newLink);
      }
    }
  } catch {
    // 读取失败则沿用默认 Logo，无需提示
  }

  const fetchUserInfo = async () => {
    try {
      console.log('[getInitialState] fetchUserInfo called, token:', getToken());
      const result = await fetchCurrentUser({
        skipErrorHandler: true,
        showType: 1,
      });
      const user = result.data; // SysUser
      let avatar = user.avatar || generateAvatar(user.id?.toString() || user.username);

      // 优先从 roleCodes 提取，兼容旧版 roles 对象列表
      const roles = user.roleCodes || (user as any).roles?.map((role: any) => typeof role === 'object' ? role.code : role).filter(Boolean) || ['ROLE_DEFAULT'];
      const permissions = user.permissions || user.permissionCodes || [];

      return {
        id: user.id?.toString(),
        name: user.username,
        realName: user.realName,
        email: user.email,
        phone: user.phone,
        gender: user.gender,
        avatar,
        roles,
        permissions
      }
    } catch (error) {
      console.log(error);
      removeToken();
      return undefined;
    }
  };
  // 如果不是登录页且有 Token，才获取用户信息
  const { location } = history;
  const isLoginPage = location.pathname === LOGIN_PATH;
  const currentUser = (!isLoginPage && getToken()) ? await fetchUserInfo() : undefined;

  return {
    fetchUserInfo,
    currentUser,
    layoutSettings: defaultSettings as Partial<LayoutSettings>,
    logoUrl,
    sysTitle,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [<NoticeIcon key="NoticeIcon" />, <Question key="doc" />, <SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar || generateAvatar(initialState?.currentUser?.id || initialState?.currentUser?.name),
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu={true}>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== LOGIN_PATH) {
        removeToken();
        history.push(LOGIN_PATH);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
        <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          <LinkOutlined />
          <span>接口文档 (OpenAPI)</span>
        </Link>,
      ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          <NoticeBanner />
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.layoutSettings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  layoutSettings: settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    menuItemRender: (menuItemProps, defaultDom) => {
      // 如果是外部链接或没有路径，使用默认渲染
      if (menuItemProps.isUrl || !menuItemProps.path) {
        return defaultDom;
      }
      // 对于所有有路径的菜单项，使用Link包装实现路由跳转，利用defaultDom处理收起状态
      return <Link to={menuItemProps.path}>{defaultDom}</Link>
    },
    menuDataRender,

    ...initialState?.layoutSettings,
    // 侧边栏 Logo 与标题使用 sys_config 动态配置（来自 getInitialState）
    logo: initialState?.logoUrl,
    title: initialState?.sysTitle,
  };
};

// export const onRouteChange = async ({location}: any) => {
//
//   if (location.pathname !== LOGIN_PATH && !isUserLoggedIn()) {
//     history.replace(LOGIN_PATH);
//   }
//
//   if (location.pathname === LOGIN_PATH && isUserLoggedIn()) {
//     console.log("onRouteChange: login");
//     // 获取 redirect 参数
//     const redirectUri = new URLSearchParams(location.search).get('redirect') || '/';
//     console.log("onRouteChange: ", redirectUri);
//     history.push(redirectUri);
//   }
//
//   if (WHITE_LIST.includes(location.pathname)) {
//     return;
//   }
// };

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};

// export function rootContainer(container: any) {
//   return (
//     <ConfigProvider theme={{
//       token: {
//         colorPrimary: '#13C2C2', // 设置主题颜色
//       },
//     }}
//     >
//       {container}
//     </ConfigProvider>
//   );
// }
